<script context="module">
  export const defaultDocKey = Symbol();
  export const docsKey = Symbol();
  export const categoriesKey = Symbol();
</script>

<script lang="ts">
  import { sineOut } from "svelte/easing";
  import { fadeSlide } from "$transitions";
  import { NodeViewWrapper } from "svelte-tiptap";
  import { get } from "svelte/store";
  import { setContext, tick, onMount } from "svelte";
  import { derived, writable } from "svelte/store";
  import { v4 as uuidv4 } from "uuid";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Row from "./Row.svelte";
  import GutterMenu from "$components/editor/extensions/+shared/GutterMenu.svelte";
  import type { NodeViewProps } from "@tiptap/core";
  import type { Item, ItemsRow } from "./DocsGalleryBlock";
  import { getStores } from "$stores";
  import { Plus } from "$components/+blocks/OVIcon";
  import { onlyDocs } from "app/stores/documents";

  export let node: NodeViewProps["node"];
  export let deleteNode: NodeViewProps["deleteNode"];
  export let updateAttributes: NodeViewProps["updateAttributes"];
  export let isEditable: boolean;
  $$restProps; // consume unused tiptap props

  const { docs } = getStores();
  const defaultDoc = writable<Item>({} as Item);
  const documents = derived(docs._documents, ($docs) =>
    Object.values($docs).filter((d) => !d.isDraft)
  );
  const categories = derived(docs.categoriesList, ($folders) =>
    $folders.filter((f) => onlyDocs(f.items).some((d) => !get(d).isDraft))
  );

  setContext(defaultDocKey, defaultDoc);
  setContext(docsKey, documents);
  setContext(categoriesKey, categories);

  onMount(() => {
    let defaultDocTitle: string | undefined;
    let defaultDocUuid: string | undefined;
    let defaultDocIcon: string | null | undefined;
    let defaultDocIconDark: string | null | undefined;

    // if there are documents in the editor, we use the first one as the default document
    if ($documents.length) {
      defaultDocTitle = $documents[0].title;
      defaultDocUuid = $documents[0].uuid;
      defaultDocIcon = $documents[0].icon;
      defaultDocIconDark = $documents[0].iconDark;
    }

    $defaultDoc = {
      cellUUID: uuidv4(),
      title: defaultDocTitle ?? "Select item",
      uuid: defaultDocUuid ?? uuidv4(),
      icon: defaultDocIcon ?? "",
      iconDark: defaultDocIconDark ?? "",
      description: "",
      type: "doc",
    };

    // if there is a first row which is empty, we add the default document, this occurs when the block is first created
    // or when the last row is deleted and added
    if (isEditable && node.attrs.rows.length && !node.attrs.rows[0].items.length) {
      updateRow(
        {
          uuid: node.attrs.rows[0].uuid,
          items: [$defaultDoc],
        },
        0
      );
    }
  });

  async function updateRow(row: ItemsRow, index: number) {
    // we need to wait for the DOM to update its pending changes before updating the node rows
    await tick();
    const rows = [...node.attrs.rows];
    rows[index] = row;

    updateAttributes({
      rows: rows.filter((row: ItemsRow) => row.items.length > 0),
    });
  }

  function addRow() {
    updateAttributes({
      rows: [
        ...node.attrs.rows,
        {
          uuid: uuidv4(),
          items: [$defaultDoc],
        },
      ],
    });
  }
</script>

<NodeViewWrapper class="not-prose group relative rounded pt-2 pb-4">
  {#if isEditable}
    <GutterMenu on:delete-block={deleteNode} />
  {/if}

  <div class="flex flex-col space-y-4">
    {#each node.attrs.rows as row, index (row.uuid)}
      <div transition:fadeSlide={{ duration: 250, easing: sineOut }}>
        <Row {row} {isEditable} on:update-row={(event) => updateRow(event.detail, index)} />
      </div>
    {/each}
  </div>

  {#if isEditable}
    <button
      class="mt-4 w-full rounded-md border border-black/10 py-2 transition-colors hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/5"
      on:click={addRow}
    >
      <OVIcon icon={Plus} class="m-auto text-neutral-400" size="w-5" />
    </button>
  {/if}
</NodeViewWrapper>
